"use client";

import { useState } from "react";
import { PillGroup, TextField } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  NOTICE,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  fixed,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-distributions/format";
import {
  Z_COLUMNS,
  Z_ROW_TENTHS,
  type ZArea,
  zArea,
  zCell,
  zCellHundredths,
  zFromArea,
} from "@/lib/tool-kit/stats-distributions/tables";

/** English strings. A translated content file overrides these through `ui`. */
export const Z_TABLE_STRINGS = {
  areaLabel: "Table shows",
  areaLeft: "Area to the left of z (cumulative)",
  areaRight: "Area to the right of z",
  areaCenter: "Area between 0 and z",
  zLookupLabel: "Look up a z-score",
  pLookupLabel: "Find z for an area",
  pLookupHint: "The area for the table type chosen above.",
  lookupHeading: "Lookup",
  leftResult: "Area to the left, P(Z < {z}) = {value}",
  rightResult: "Area to the right, P(Z > {z}) = {value}",
  centerResult: "Area between 0 and {z} = {value}",
  zForLeft: "P(Z < z) = {p} at z = {z}",
  zForRight: "P(Z > z) = {p} at z = {z}",
  zForCenter: "The area between 0 and z is {p} at z = ±{z}",
  offTable:
    "|z| is above 3.49, beyond the printed table — the areas above are still exact.",
  enterZ: "Enter a z-score, such as 1.96.",
  enterP:
    "Enter an area between 0 and 1 — below 0.5 for the area between 0 and z.",
  negativeCaption: "Negative z: {kind}",
  positiveCaption: "Positive z: {kind}",
  centerCaption: "Area between the mean (z = 0) and z",
  kindLeft: "area to the left of z",
  kindRight: "area to the right of z",
  zHeader: "z",
  print: "Print table",
  printNote:
    "Values are areas under the standard normal curve, rounded to four decimals. For negative z in the area-between table, use |z|.",
} as const;

type Strings = typeof Z_TABLE_STRINGS;

const HIGHLIGHT = "bg-primary/15 font-semibold text-foreground";
const HEADER_HIGHLIGHT = "bg-primary/10";

interface Selected {
  hundredths: number;
}

function rowLabel(rowTenths: number, negative: boolean, fmt: NumberFormatter) {
  const text = fmt(rowTenths / 10, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  return negative ? `−${text}` : text;
}

function ZTableBlock({
  negative,
  area,
  caption,
  selected,
  strings,
  fmt,
}: {
  negative: boolean;
  area: ZArea;
  caption: string;
  selected: Selected | null;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const rows = negative ? [...Z_ROW_TENTHS].reverse() : Z_ROW_TENTHS;
  const cell = selected ? zCell(selected.hundredths / 100) : null;
  const inThisTable =
    cell !== null && (cell.negative === negative || area === "center");
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card print:break-inside-avoid print:overflow-visible">
      <table className="w-full border-collapse font-mono text-sm tabular-nums">
        <caption className="px-3 pt-3 pb-1 text-left font-medium font-sans text-foreground text-sm">
          {caption}
        </caption>
        <thead>
          <tr className="border-border border-b">
            <th
              className="sticky left-0 bg-card px-2 py-1.5 text-left font-semibold"
              scope="col"
            >
              {strings.zHeader}
            </th>
            {Z_COLUMNS.map((column) => (
              <th
                className={`whitespace-nowrap px-2 py-1.5 text-right font-semibold ${inThisTable && cell?.column === column ? HEADER_HIGHLIGHT : ""}`}
                key={column}
                scope="col"
              >
                {fixed(column / 100, 2, fmt, false)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((rowTenths) => {
            const rowSelected = inThisTable && cell?.rowTenths === rowTenths;
            return (
              <tr
                className="border-border/60 border-b last:border-0"
                key={rowTenths}
              >
                <th
                  className={`sticky left-0 whitespace-nowrap bg-card px-2 py-1 text-left font-semibold ${rowSelected ? HEADER_HIGHLIGHT : ""}`}
                  scope="row"
                >
                  {rowLabel(rowTenths, negative, fmt)}
                </th>
                {Z_COLUMNS.map((column) => {
                  const z = zCellHundredths(rowTenths, column, negative) / 100;
                  const hit = rowSelected && cell?.column === column;
                  return (
                    <td
                      className={`whitespace-nowrap px-2 py-1 text-right text-muted-foreground ${hit ? HIGHLIGHT : ""}`}
                      key={column}
                    >
                      {fmt(zArea(z, area), {
                        minimumFractionDigits: 4,
                        maximumFractionDigits: 4,
                      })}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Lookup({
  area,
  zText,
  pText,
  strings,
  fmt,
}: {
  area: ZArea;
  zText: string;
  pText: string;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const z = parseNumberInput(zText);
  const p = parseNumberInput(pText);
  const zFound = p === null ? null : zFromArea(p, area);
  const five = (value: number) =>
    fmt(value, { minimumFractionDigits: 5, maximumFractionDigits: 5 });
  const zShown = z === null ? "" : fmt(z, { maximumFractionDigits: 4 });
  const forArea: Record<ZArea, string> = {
    left: strings.zForLeft,
    right: strings.zForRight,
    center: strings.zForCenter,
  };
  return (
    <div
      aria-live="polite"
      className={`${CARD} space-y-2 text-sm print:hidden`}
    >
      <h2 className={WIDGET_H2}>{strings.lookupHeading}</h2>
      {z === null ? (
        <p className="text-muted-foreground">{strings.enterZ}</p>
      ) : (
        <ul className="space-y-1 font-mono text-foreground">
          <li>
            {t(strings.leftResult, {
              z: zShown,
              value: five(zArea(z, "left")),
            })}
          </li>
          <li>
            {t(strings.rightResult, {
              z: zShown,
              value: five(zArea(z, "right")),
            })}
          </li>
          <li>
            {t(strings.centerResult, {
              z: zShown,
              value: five(zArea(z, "center")),
            })}
          </li>
        </ul>
      )}
      {z !== null && Math.abs(z) > 3.49 ? (
        <p className="text-muted-foreground">{strings.offTable}</p>
      ) : null}
      {zFound === null || p === null ? (
        <p className="text-muted-foreground">{strings.enterP}</p>
      ) : (
        <p className="font-mono text-foreground">
          {t(forArea[area], {
            p: fmt(p, { maximumFractionDigits: 6 }),
            z: fmt(area === "center" ? Math.abs(zFound) : zFound, {
              minimumFractionDigits: 4,
              maximumFractionDigits: 4,
            }),
          })}
        </p>
      )}
    </div>
  );
}

export default function ZTableWidget() {
  const strings = useToolUi(Z_TABLE_STRINGS);
  const fmt = useToolNumber();
  const [area, setArea] = useState<ZArea>("left");
  const [zText, setZText] = useState("1.96");
  const [pText, setPText] = useState("0.975");

  const z = parseNumberInput(zText);
  const selected = z === null ? null : { hundredths: Math.round(z * 100) };
  const kind = area === "right" ? strings.kindRight : strings.kindLeft;

  return (
    <>
      <section className="space-y-5 print:hidden">
        <PillGroup
          label={strings.areaLabel}
          name="zt-area"
          onChange={setArea}
          options={[
            { value: "left", label: strings.areaLeft },
            { value: "right", label: strings.areaRight },
            { value: "center", label: strings.areaCenter },
          ]}
          value={area}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="zt-z"
            label={strings.zLookupLabel}
            numeric
            onChange={setZText}
            value={zText}
          />
          <TextField
            hint={strings.pLookupHint}
            id="zt-p"
            label={strings.pLookupLabel}
            numeric
            onChange={setPText}
            value={pText}
          />
        </div>
      </section>
      <Lookup
        area={area}
        fmt={fmt}
        pText={pText}
        strings={strings}
        zText={zText}
      />
      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
          <p className={`${NOTICE} flex-1`}>{strings.printNote}</p>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => window.print()}
            type="button"
          >
            {strings.print}
          </button>
        </div>
        {area === "center" ? (
          <ZTableBlock
            area={area}
            caption={strings.centerCaption}
            fmt={fmt}
            negative={false}
            selected={selected}
            strings={strings}
          />
        ) : (
          <>
            <ZTableBlock
              area={area}
              caption={t(strings.negativeCaption, { kind })}
              fmt={fmt}
              negative
              selected={selected}
              strings={strings}
            />
            <ZTableBlock
              area={area}
              caption={t(strings.positiveCaption, { kind })}
              fmt={fmt}
              negative={false}
              selected={selected}
              strings={strings}
            />
          </>
        )}
      </section>
    </>
  );
}
